import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { API_CONFIG } from '../../config/apiConfig';
import { InterestsAssessmentStep, PreferencesAssessmentStep, ReviewAssessmentStep } from './AssessmentSteps';
import './CareerAssessment.css';

const SKILL_CATEGORY_ICON_FALLBACKS = {
  'digital skills': '💻',
  'healthcare skills': '🏥'
};

const getSkillCategoryIcon = (category) => category.icon ||
  SKILL_CATEGORY_ICON_FALLBACKS[category.name?.trim().toLowerCase()] || '';

// Utility function to make API calls with direct URL (proxy not working)
const makeAPICall = async (endpoint, options = {}) => {
  console.log(`🔄 Making API call to: ${endpoint}`);
  console.log(`🔧 Request options:`, options);

  // Use direct URL since proxy is not working
  const directUrl = `http://localhost:8000${endpoint}`;
  console.log(`🔄 Direct URL: ${directUrl}`);

  try {
    const response = await fetch(directUrl, options);
    console.log(`📡 Direct response:`, {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      headers: Object.fromEntries(response.headers.entries())
    });

    if (response.ok) {
      console.log(`✅ Direct call successful for ${endpoint}`);
    } else {
      console.log(`❌ Direct call failed for ${endpoint}: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error(`❌ Direct call failed for ${endpoint}:`, error);
    throw error;
  }
};

const CareerAssessment = () => {
  const navigate = useNavigate();
  const { accessToken, makeAuthenticatedRequest } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [assessmentData, setAssessmentData] = useState({
    skills: [],
    interests: {
      passport_interests: {},
      career_field_interests: {}
    },
    preferences: {}
  });
  const [skillCategories, setSkillCategories] = useState([]);
  const [profileSkillRecords, setProfileSkillRecords] = useState([]);
  const [careerFields, setCareerFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const scrollToTop = () => {
    // Keep the assessment header visible below the fixed global navigation.
    const assessmentHeader = document.querySelector('.assessment-header');
    if (assessmentHeader) {
      const navigationOffset = 84;
      const targetTop = Math.max(
        0,
        assessmentHeader.getBoundingClientRect().top + window.scrollY - navigationOffset
      );
      window.scrollTo({ top: targetTop, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    loadAssessmentData();
    loadUserProfileSkills();
    startAssessmentSession();
    // Scroll to top when component loads
    scrollToTop();
  }, []);

  useEffect(() => {
    if (!profileSkillRecords.length || !skillCategories.length) return;
    const assessmentSkillIdsByLmsId = new Map(skillCategories.flatMap(category => category.skills || [])
      .filter(skill => skill.lms_skill_id)
      .map(skill => [String(skill.lms_skill_id), skill.id]));
    const proficiencyMapping = { beginner: 2, intermediate: 3, advanced: 4, expert: 5 };
    const prePopulatedSkills = profileSkillRecords.map(userSkill => ({
      skill_id: assessmentSkillIdsByLmsId.get(String(userSkill.skill_lms_id || '')),
      proficiency_level: proficiencyMapping[userSkill.proficiency_level] || 2,
      years_experience: userSkill.years_experience || 0,
      is_certified: false
    })).filter(skill => skill.skill_id);
    setAssessmentData(previous => ({ ...previous, skills: prePopulatedSkills }));
  }, [profileSkillRecords, skillCategories]);

  // Scroll to top whenever the current step changes
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  const loadAssessmentData = async () => {
    try {
      console.log('🔍 Loading assessment data...');

      const response = await makeAPICall('/api/career/assessment-data/');
      console.log('📡 Assessment data response:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Assessment data loaded:', {
          skillCategories: data.skill_categories?.length || 0,
          careerFields: data.career_fields?.length || 0,
          totalSkills: data.skill_categories?.reduce((total, cat) => total + (cat.skills?.length || 0), 0) || 0
        });
        setSkillCategories(data.skill_categories || []);
        setCareerFields(data.career_fields || []);
      } else {
        console.error('❌ Failed to load assessment data:', response.status);
        const errorText = await response.text();
        console.error('❌ Error details:', errorText);
      }
    } catch (error) {
      console.error('❌ Error loading assessment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProfileSkills = async () => {
    try {
      console.log('🔍 Loading user profile skills for pre-population...');

      // Check if user is authenticated
      const token = accessToken || localStorage.getItem('access_token');
      if (!token) {
        console.log('ℹ️ No token found, skipping user skills pre-population');
        return;
      }

      const response = await makeAuthenticatedRequest(`${API_CONFIG.API_URL}/auth/user-skills/`);
      console.log('📡 User skills response:', response.status, response.statusText);

      if (response.ok) {
        const userSkillsData = await response.json();
        console.log('✅ User profile skills loaded:', userSkillsData);

        // Handle different response formats
        let userSkillsArray = [];
        if (Array.isArray(userSkillsData)) {
          userSkillsArray = userSkillsData;
        } else if (userSkillsData && Array.isArray(userSkillsData.results)) {
          userSkillsArray = userSkillsData.results;
        } else if (userSkillsData && Array.isArray(userSkillsData.user_skills)) {
          userSkillsArray = userSkillsData.user_skills;
        }

        if (userSkillsArray.length > 0) setProfileSkillRecords(userSkillsArray);
      } else if (response.status === 401) {
        console.log('ℹ️ User not authenticated, skipping skills pre-population');
      } else {
        console.error('❌ Failed to load user skills:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Error loading user profile skills:', error);
      // Don't show error to user as this is optional pre-population
    }
  };

  const startAssessmentSession = async () => {
    try {
      console.log('🚀 Starting assessment session...');

      const response = await makeAuthenticatedRequest(`${API_CONFIG.career.base()}/assessment/start/`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Assessment session started:', data.session_id);
        setSessionId(data.session_id);
      } else {
        console.error('❌ Failed to start assessment session:', response.status);
      }
    } catch (error) {
      console.error('❌ Error starting assessment session:', error);
    }
  };

  const updateProgress = async () => {
    if (!sessionId) return;
    
    const completionPercentage = (currentStep / 4) * 100;
    
    try {
      await makeAuthenticatedRequest(`${API_CONFIG.career.base()}/assessment/progress/`, {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          assessment_data: assessmentData,
          completion_percentage: completionPercentage
        })
      });
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleSkillChange = (skillId, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      skills: prev.skills.map(skill => 
        skill.skill_id === skillId 
          ? { ...skill, [field]: value }
          : skill
      ).concat(
        prev.skills.find(s => s.skill_id === skillId) 
          ? [] 
          : [{ skill_id: skillId, [field]: value, proficiency_level: 1, is_certified: false }]
      )
    }));
  };

  const handleInterestChange = (fieldId, level) => {
    setAssessmentData(prev => ({
      ...prev,
      interests: {
        ...prev.interests,
        career_field_interests: {
          ...prev.interests.career_field_interests,
          [fieldId]: level
        }
      }
    }));
  };

  const handlePassportInterestChange = (passportId, level) => {
    setAssessmentData(prev => ({
      ...prev,
      interests: {
        ...prev.interests,
        passport_interests: {
          ...prev.interests.passport_interests,
          [passportId]: level
        }
      }
    }));
  };

  const handlePreferenceChange = (field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      interests: {
        ...prev.interests,
        [field]: value
      }
    }));
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      updateProgress();
      scrollToTop();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      scrollToTop();
    }
  };

  const validateAssessment = () => {
    const errors = [];

    // Check if we have a session ID
    if (!sessionId) {
      errors.push("Assessment session not initialized. Please refresh the page and try again.");
    }

    // Validate skills assessment
    const skillsWithProficiency = assessmentData.skills.filter(skill =>
      skill.proficiency_level && skill.proficiency_level > 0
    );

    if (skillsWithProficiency.length === 0) {
      errors.push("Step 1 - Skills Assessment: Please rate your proficiency in at least one skill.");
    }

    // Validate career interests
    const careerFieldInterests = assessmentData.interests?.career_field_interests || {};
    const passportInterests = assessmentData.interests?.passport_interests || {};
    const hasCareerInterests = Object.keys(careerFieldInterests).length > 0 || Object.keys(passportInterests).length > 0;

    if (!hasCareerInterests) {
      errors.push("Step 2 - Career Interests: Please rate your interest in at least one Passport pathway or career field.");
    }

    // Validate work environment preferences
    const workEnvPrefs = assessmentData.interests?.work_environment_preferences || {};
    const hasWorkEnvPrefs = Object.keys(workEnvPrefs).length > 0;

    if (!hasWorkEnvPrefs) {
      errors.push("Step 3 - Preferences: Please select at least one work environment preference.");
    }

    // Validate study preferences
    const hasStudyFormat = assessmentData.interests?.preferred_study_format;
    if (!hasStudyFormat) {
      errors.push("Step 3 - Preferences: Please select your preferred study format.");
    }

    return errors;
  };

  const submitAssessment = async () => {
    // Prevent multiple submissions
    if (submitting) return;

    // Validate assessment before submission
    const validationErrors = validateAssessment();

    if (validationErrors.length > 0) {
      const errorMessage = "Please complete the following before submitting:\n\n" +
        validationErrors.map((error, index) => `${index + 1}. ${error}`).join('\n') +
        "\n\nClick OK to review your responses.";

      alert(errorMessage);

      // Navigate to the first incomplete step
      if (validationErrors.some(error => error.includes('Step 1'))) {
        setCurrentStep(1);
      } else if (validationErrors.some(error => error.includes('Step 2'))) {
        setCurrentStep(2);
      } else if (validationErrors.some(error => error.includes('Step 3'))) {
        setCurrentStep(3);
      }

      scrollToTop();
      return;
    }

    setSubmitting(true);

    try {
      const submissionData = {
        session_id: sessionId,
        skills_assessment: assessmentData.skills,
        career_interests: assessmentData.interests,
        additional_notes: assessmentData.notes || ''
      };

      console.log('🚀 Submitting assessment data:', submissionData);
      console.log('📊 Skills count:', assessmentData.skills.length);
      console.log('💡 Interests:', assessmentData.interests);
      console.log('🔍 Detailed submission data:', JSON.stringify(submissionData, null, 2));

      const response = await makeAuthenticatedRequest(`${API_CONFIG.career.base()}/assessment/submit/`, {
        method: 'POST',
        body: JSON.stringify(submissionData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Assessment submitted successfully:', result);

        // Show success message
        const recommendationsCount = result.recommendations_generated || 0;
        console.log(`🎯 Recommendations generated: ${recommendationsCount}`);

        if (recommendationsCount > 0) {
          alert(`🎉 Assessment completed successfully!\n\nGenerated ${recommendationsCount} course recommendations for you.`);

          navigate('/career/recommendations', {
            state: { recommendationsGenerated: recommendationsCount }
          });
        } else {
          console.warn('⚠️ No recommendations were generated');
          alert(`⚠️ Assessment completed, but no course recommendations were generated.\n\nThis might be because:\n- No courses match your current profile\n- Your assessment needs more specific information\n\nYou can retake the assessment or contact support for help.`);

          navigate('/career', {
            state: { assessmentCompleted: true, noRecommendations: true }
          });
        }
      } else {
        let errorMessage = 'Failed to submit assessment. ';
        
        try {
          // Read body as text first to avoid "body stream already read" error
          const responseText = await response.text();
          
          try {
            // Try to parse as JSON
            const errorData = JSON.parse(responseText);
            if (errorData.detail) {
              errorMessage += errorData.detail;
            } else if (errorData.error) {
              errorMessage += errorData.error;
            } else {
              errorMessage += `Server returned error ${response.status}.`;
            }
          } catch (jsonError) {
            // If not JSON, use the text content (truncated if too long)
            const cleanText = responseText.replace(/<[^>]*>/g, '').substring(0, 200);
            errorMessage += cleanText || `Server returned error ${response.status}.`;
          }
        } catch (readError) {
          errorMessage += `Server returned error ${response.status}.`;
        }

        console.error('❌ Assessment submission failed:', response.status, errorMessage);
        alert(`❌ ${errorMessage}\n\nPlease try again or contact support if the problem persists.`);
      }
    } catch (error) {
      console.error('❌ Error submitting assessment:', error);

      let userMessage = 'An unexpected error occurred while submitting your assessment.';

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        userMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (error.message) {
        userMessage += ` Error: ${error.message}`;
      }

      alert(`❌ ${userMessage}\n\nPlease try again or contact support if the problem persists.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="career-assessment-loading">
        <div className="loading-spinner"></div>
        <p>Loading assessment...</p>
      </div>
    );
  }

  // Debug logging
  console.log('🔍 CareerAssessment render state:', {
    loading,
    skillCategoriesCount: skillCategories.length,
    careerFieldsCount: careerFields.length,
    currentStep,
    sessionId
  });

  return (
    <div className="career-assessment">
      <div className="assessment-header">
        <h1>🎯 Career Assessment</h1>
        <p>Help us understand your skills, interests, and goals to recommend the best training programs for you.</p>
        
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${(currentStep / 4) * 100}%` }}></div>
        </div>
        <div className="step-indicator">
          Step {currentStep} of 4
        </div>
      </div>

      <div className="assessment-content">
        {currentStep === 1 && (
          <SkillsAssessmentStep
            skillCategories={skillCategories}
            assessmentData={assessmentData}
            onSkillChange={handleSkillChange}
            onRetryLoadData={loadAssessmentData}
          />
        )}

        {currentStep === 2 && (
          <InterestsAssessmentStep 
            careerFields={careerFields}
            assessmentData={assessmentData}
            onInterestChange={handleInterestChange}
            onPassportInterestChange={handlePassportInterestChange}
          />
        )}

        {currentStep === 3 && (
          <PreferencesAssessmentStep 
            assessmentData={assessmentData}
            onPreferenceChange={handlePreferenceChange}
          />
        )}

        {currentStep === 4 && (
          <ReviewAssessmentStep 
            assessmentData={assessmentData}
            skillCategories={skillCategories}
            careerFields={careerFields}
          />
        )}
      </div>

      <div className={`assessment-navigation ${currentStep === 4 ? 'assessment-navigation--stacked' : ''}`}>
        {currentStep > 1 && (
          <button className="btn-secondary" onClick={prevStep}>
            ← Previous
          </button>
        )}

        {currentStep < 4 ? (
          <button className="btn-primary" onClick={nextStep}>
            Next →
          </button>
        ) : (
          <button
            className="btn-primary"
            onClick={submitAssessment}
            disabled={submitting}
            style={{
              opacity: submitting ? 0.7 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Submitting... ⏳' : 'Complete Assessment 🚀'}
          </button>
        )}
      </div>
    </div>
  );
};

// Skills Assessment Step Component
const SkillsAssessmentStep = ({ skillCategories, assessmentData, onSkillChange, onRetryLoadData }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [skillSearch, setSkillSearch] = useState('');

  // Debug logging
  console.log('🔍 SkillsAssessmentStep render:', {
    skillCategoriesCount: skillCategories?.length || 0,
    skillCategories: skillCategories?.map(cat => ({ id: cat.id, name: cat.name, skillsCount: cat.skills?.length || 0 })),
    selectedCategory,
    hasRetryFunction: typeof onRetryLoadData === 'function'
  });

  useEffect(() => {
    if (skillCategories.length > 0 && !selectedCategory) {
      console.log('🎯 Setting initial category:', skillCategories[0].id, skillCategories[0].name);
      setSelectedCategory(skillCategories[0].id);
    }
  }, [skillCategories, selectedCategory]);

  const currentCategory = skillCategories.find(cat => cat.id === selectedCategory);
  const normalizedSearch = skillSearch.trim().toLocaleLowerCase();
  const isSearching = normalizedSearch.length > 0;
  const visibleCategories = isSearching
    ? skillCategories.map(category => ({
        ...category,
        skills: (category.skills || []).filter(skill =>
          `${skill.name || ''} ${skill.description || ''} ${category.name || ''}`
            .toLocaleLowerCase()
            .includes(normalizedSearch)
        )
      })).filter(category => category.skills.length > 0)
    : currentCategory ? [currentCategory] : [];
  const matchingSkillCount = visibleCategories.reduce((count, category) => count + category.skills.length, 0);

  return (
    <div className="skills-assessment-step">
      <h2>📚 Skills Assessment</h2>
      <p>Rate your proficiency in different skills. This helps us match you with suitable training programs.</p>

      <div className="skills-search">
        <label htmlFor="skills-assessment-search">Search skills across all categories</label>
        <div className="skills-search-control">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="10.8" cy="10.8" r="6.8" />
            <path d="m16 16 4.5 4.5" />
          </svg>
          <input
            id="skills-assessment-search"
            type="search"
            value={skillSearch}
            onChange={event => setSkillSearch(event.target.value)}
            placeholder="Search by skill or category..."
            autoComplete="off"
          />
          {skillSearch && (
            <button
              type="button"
              className="skills-search-clear"
              onClick={() => setSkillSearch('')}
              aria-label="Clear skill search"
            >
              ×
            </button>
          )}
        </div>
        <p className="skills-search-status" role="status" aria-live="polite">
          {isSearching
            ? `${matchingSkillCount} ${matchingSkillCount === 1 ? 'skill' : 'skills'} found across ${visibleCategories.length} ${visibleCategories.length === 1 ? 'category' : 'categories'}`
            : `Search ${skillCategories.reduce((count, category) => count + (category.skills?.length || 0), 0)} skills across all categories`}
        </p>
      </div>

      {skillCategories.length === 0 ? (
        <div className="no-data-message">
          <p>⚠️ No skill categories available. Please check if the assessment data has been loaded.</p>
          <div style={{ marginTop: '20px' }}>
            <button onClick={() => window.location.reload()} className="btn-secondary">
              Reload Page
            </button>
            <button
              onClick={onRetryLoadData || (() => window.location.reload())}
              className="btn-secondary"
              style={{ marginLeft: '10px' }}
            >
              Retry Loading Data
            </button>
            <button
              onClick={() => {
                console.log('🔍 SkillsAssessmentStep state:', {
                  skillCategoriesCount: skillCategories.length,
                  skillCategories: skillCategories.map(cat => ({
                    id: cat.id,
                    name: cat.name,
                    skillsCount: cat.skills?.length || 0
                  })),
                  hasRetryFunction: typeof onRetryLoadData === 'function'
                });
                console.log('🔍 Full skillCategories:', skillCategories);
              }}
              className="btn-secondary"
              style={{ marginLeft: '10px' }}
            >
              Debug Skills
            </button>
          </div>
          <div style={{ marginTop: '15px', fontSize: '14px', color: '#666' }}>
            <p><strong>Troubleshooting:</strong></p>
            <p>1. Check browser console (F12) for error messages</p>
            <p>2. Verify backend server is running on port 8000</p>
            <p>3. Test API directly: <a href="http://localhost:8000/api/career/assessment-data/" target="_blank" rel="noreferrer">http://localhost:8000/api/career/assessment-data/</a></p>
          </div>
        </div>
      ) : !isSearching ? (
        <div className="category-tabs">
          {skillCategories.map(category => (
            <button
              key={category.id}
              className={`category-tab ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.id)}
            >
              <span className="category-icon" aria-hidden="true">{getSkillCategoryIcon(category)}</span>
              {category.name}
            </button>
          ))}
        </div>
      ) : null}

      {visibleCategories.map(category => (
        <section className="skills-category-results" key={category.id}>
          {isSearching && (
            <h3 className="skills-category-results-heading">
              {category.name}<span>{category.skills.length}</span>
            </h3>
          )}
          {!category.skills?.length && !isSearching ? (
            <div className="no-skills-message">
              <p>⚠️ No skills available in this category.</p>
              <p>Category: {category.name}</p>
            </div>
          ) : (
            <div className="skills-grid">
              {category.skills.map(skill => {
              const userSkill = assessmentData.skills.find(s => s.skill_id === skill.id) || {};

              return (
                <div key={skill.id} className="skill-card">
                  <h4>{skill.name}</h4>
                  <p>{skill.description}</p>

                  <div className="proficiency-selector">
                    <label>Proficiency Level:</label>
                    <div className="proficiency-levels">
                      {[1, 2, 3, 4, 5].map(level => (
                        <button
                          key={level}
                          className={`proficiency-btn ${userSkill.proficiency_level === level ? 'active' : ''}`}
                          onClick={() => onSkillChange(skill.id, 'proficiency_level', level)}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                    <small>1 = Beginner, 5 = Expert</small>
                  </div>

                  <div className="experience-input">
                    <label>Years of Experience:</label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="0.5"
                      value={userSkill.years_experience || ''}
                      onChange={(e) => onSkillChange(skill.id, 'years_experience', parseFloat(e.target.value))}
                      placeholder="0"
                    />
                  </div>

                  <div className="certification-check">
                    <label>
                      <input
                        type="checkbox"
                        checked={userSkill.is_certified || false}
                        onChange={(e) => onSkillChange(skill.id, 'is_certified', e.target.checked)}
                      />
                      I have certification in this skill
                    </label>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </section>
      ))}

      {isSearching && visibleCategories.length === 0 && (
        <div className="no-skills-message" role="status">
          <p>No skills match “{skillSearch}”. Try another search.</p>
        </div>
      )}
    </div>
  );
};

export default CareerAssessment;
